"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCircleInfoOutline18 } from "nucleo-ui-outline-18";
import type { WorkflowInput } from "@/stores/workflowsStore";
import { formatLabel } from "@/utils/string";
import type { Edge, Node } from "@xyflow/react";
import { useCallback, useMemo } from "react";
import { useWorkflow } from "../../_hooks/useWorkflow";
import BooleanInput from "./BooleanInput";
import JsonInput from "./JsonInput";
import VariableInput from "./VariableInput";
interface NodeDefinition {
  type: string;
  resultSchema?: {
    properties?: Record<string, {
      description?: string;
      type?: string;
    }>;
  };
}
interface WorkflowPayloadFieldsProps {
  label?: string;
  description?: string;
  value: Record<string, unknown> | string;
  onChange: (value: Record<string, unknown>) => void;
  workflowId?: string;
  currentNodeId?: string;
  allNodes?: Node[];
  allEdges?: Edge[];
  nodeDefinitions?: NodeDefinition[];
}
export default function WorkflowPayloadFields(t0) {
  const $ = _c(47);
  const {
    label,
    value,
    onChange,
    workflowId,
    currentNodeId,
    allNodes,
    allEdges,
    nodeDefinitions
  } = t0;
  const targetWorkflowQuery = useWorkflow(workflowId ?? null);
  const targetWorkflow = targetWorkflowQuery.data;
  const inputSchema = targetWorkflow?.definition?.inputSchema;
  let t1;
  bb0: {
    if (!value) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {};
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    if (typeof value === "string") {
      try {
        let t2;
        if ($[1] !== value) {
          t2 = JSON.parse(value);
          $[1] = value;
          $[2] = t2;
        } else {
          t2 = $[2];
        }
        t1 = t2;
        break bb0;
      } catch {
        let t2;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = {};
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        t1 = t2;
        break bb0;
      }
    }
    t1 = value as Record<string, unknown>;
  }
  const payloadObj = t1;
  let t2;
  if ($[4] !== onChange || $[5] !== payloadObj) {
    t2 = (key, fieldValue) => {
      onChange({
        ...payloadObj,
        [key]: fieldValue
      });
    };
    $[4] = onChange;
    $[5] = payloadObj;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleFieldChange = t2;
  if (!workflowId || !inputSchema || inputSchema.length === 0) {
    let t3;
    if ($[7] !== label) {
      t3 = label && <label className="field-label block">{label}</label>;
      $[7] = label;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== workflowId) {
      t4 = !workflowId && <p className="caption-xs">Select a target workflow first.</p>;
      $[9] = workflowId;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== inputSchema || $[12] !== onChange || $[13] !== value || $[14] !== workflowId) {
      t5 = workflowId && (!inputSchema || inputSchema.length === 0) && <div className="space-y-2"><p className="caption-xs">No inputs declared on the target workflow. You can still pass custom data:</p><JsonInput label="" value={typeof value === "string" ? value : JSON.stringify(value ?? {}, null, 2)} onChange={v => {
          try {
            onChange(JSON.parse(v));
          } catch {}
        }} placeholder="{}" /></div>;
      $[11] = inputSchema;
      $[12] = onChange;
      $[13] = value;
      $[14] = workflowId;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
      t6 = <div className="space-y-2">{t3}{t4}{t5}</div>;
      $[16] = t3;
      $[17] = t4;
      $[18] = t5;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    return t6;
  }
  let t3;
  if ($[20] !== label) {
    t3 = label && <label className="field-label block">{label}</label>;
    $[20] = label;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <IconCircleInfoOutline18 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-blue-500" />;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== targetWorkflow.name) {
    t5 = <span className="font-semibold">{targetWorkflow.name}</span>;
    $[23] = targetWorkflow.name;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <code className="rounded bg-blue-100 px-1 dark:bg-blue-900/40">{"{{variable}}"}</code>;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t5) {
    t7 = <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-800 dark:bg-blue-950/30">{t4}<p className="text-xs text-blue-700 dark:text-blue-400">Inputs declared by {t5}. Use{" "}{t6} syntax to pass dynamic values.</p></div>;
    $[26] = t5;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== allEdges || $[29] !== allNodes || $[30] !== currentNodeId || $[31] !== handleFieldChange || $[32] !== inputSchema || $[33] !== nodeDefinitions || $[34] !== payloadObj) {
    let t9;
    if ($[36] !== allEdges || $[37] !== allNodes || $[38] !== currentNodeId || $[39] !== handleFieldChange || $[40] !== nodeDefinitions || $[41] !== payloadObj) {
      t9 = (input, index) => <InputField key={`${index}-${input.key}`} input={input} value={payloadObj[input.key]} onChange={val => handleFieldChange(input.key, val)} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} />;
      $[36] = allEdges;
      $[37] = allNodes;
      $[38] = currentNodeId;
      $[39] = handleFieldChange;
      $[40] = nodeDefinitions;
      $[41] = payloadObj;
      $[42] = t9;
    } else {
      t9 = $[42];
    }
    t8 = inputSchema.map(t9);
    $[28] = allEdges;
    $[29] = allNodes;
    $[30] = currentNodeId;
    $[31] = handleFieldChange;
    $[32] = inputSchema;
    $[33] = nodeDefinitions;
    $[34] = payloadObj;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[43] !== t3 || $[44] !== t7 || $[45] !== t8) {
    t9 = <div className="space-y-3">{t3}{t7}{t8}</div>;
    $[43] = t3;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  return t9;
}

/** Renders a single input field based on its WorkflowInput schema. */
function InputField(t0) {
  const $ = _c(33);
  const {
    input,
    value,
    onChange,
    currentNodeId,
    allNodes,
    allEdges,
    nodeDefinitions
  } = t0;
  const fieldLabel = formatLabel(input.key) + (input.required ? " *" : "");
  switch (input.type) {
    case "boolean":
      {
        const t1 = value as boolean ?? false;
        let t2;
        if ($[0] !== fieldLabel || $[1] !== input.description || $[2] !== onChange || $[3] !== t1) {
          t2 = <BooleanInput label={fieldLabel} description={input.description} value={t1} onChange={onChange} />;
          $[0] = fieldLabel;
          $[1] = input.description;
          $[2] = onChange;
          $[3] = t1;
          $[4] = t2;
        } else {
          t2 = $[4];
        }
        return t2;
      }
    case "number":
      {
        const t1 = value != null ? String(value) : "";
        let t2;
        if ($[5] !== onChange) {
          t2 = v => {
            const num = Number(v);
            onChange(!v || isNaN(num) ? v : num);
          };
          $[5] = onChange;
          $[6] = t2;
        } else {
          t2 = $[6];
        }
        const t3 = `Enter ${input.key}`;
        let t4;
        if ($[7] !== allEdges || $[8] !== allNodes || $[9] !== currentNodeId || $[10] !== fieldLabel || $[11] !== input.description || $[12] !== nodeDefinitions || $[13] !== t1 || $[14] !== t2 || $[15] !== t3) {
          t4 = <VariableInput label={fieldLabel} description={input.description} value={t1} onChange={t2} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} placeholder={t3} />;
          $[7] = allEdges;
          $[8] = allNodes;
          $[9] = currentNodeId;
          $[10] = fieldLabel;
          $[11] = input.description;
          $[12] = nodeDefinitions;
          $[13] = t1;
          $[14] = t2;
          $[15] = t3;
          $[16] = t4;
        } else {
          t4 = $[16];
        }
        return t4;
      }
    case "object":
      {}
    case "array":
      {
        const t1 = value ?? (input.type === "array" ? "[]" : "{}");
        const t2 = input.type === "array" ? "[]" : "{}";
        let t3;
        if ($[17] !== fieldLabel || $[18] !== input.description || $[19] !== onChange || $[20] !== t1 || $[21] !== t2) {
          t3 = <JsonInput label={fieldLabel} description={input.description} value={t1} onChange={onChange} placeholder={t2} />;
          $[17] = fieldLabel;
          $[18] = input.description;
          $[19] = onChange;
          $[20] = t1;
          $[21] = t2;
          $[22] = t3;
        } else {
          t3 = $[22];
        }
        return t3;
      }
    case "string":
      {}
    default:
      {
        const t1 = typeof value === "string" ? value : String(value ?? "");
        const t2 = `Enter ${input.key}`;
        let t3;
        if ($[23] !== allEdges || $[24] !== allNodes || $[25] !== currentNodeId || $[26] !== fieldLabel || $[27] !== input.description || $[28] !== nodeDefinitions || $[29] !== onChange || $[30] !== t1 || $[31] !== t2) {
          t3 = <VariableInput label={fieldLabel} description={input.description} value={t1} onChange={onChange} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} placeholder={t2} />;
          $[23] = allEdges;
          $[24] = allNodes;
          $[25] = currentNodeId;
          $[26] = fieldLabel;
          $[27] = input.description;
          $[28] = nodeDefinitions;
          $[29] = onChange;
          $[30] = t1;
          $[31] = t2;
          $[32] = t3;
        } else {
          t3 = $[32];
        }
        return t3;
      }
  }
}